// 주문 상세 화면.
// 주문 직후(state.justCreated) 진입 시 성공 배너를 보여주고, 그 외에는 일반 상세로 표시한다.
// received 단계에서만 손님이 직접 취소할 수 있다 (그 이후는 매장 문의 안내).
//
// 손님 카피 톤([[feedback-customer-copy-tone]]): REJECTED/CANCELED 는 모두 "취소" 로 보인다 (shared 라벨맵 기준).

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as PortOne from "@portone/browser-sdk/v2";
import {
  ORDER_STATUS,
  ORDER_STATUS_LABEL_KO,
  ORDER_TYPE_LABEL_KO,
  PAYMENT_METHOD_TYPE,
  formatCartLineLabel,
  formatDesiredPickup,
  formatWeightG,
  lineExpectedGramsG,
  portoneCustomerId,
  stablePgPaymentId,
  type OrderDto,
} from "@oo/shared";
import { useConfirm } from "@oo/ui";
import { ApiError } from "@/api/client.ts";
import { cancelOrder, confirmDirectPayment, getOrder } from "@/features/orders/ordersApi.ts";
import {
  NATIVE_PAYMENT_APP_SCHEME,
  clearPendingDirectPayment,
  directPaymentNoticeUrls,
  directPaymentRedirectUrl,
  markPendingDirectPaymentRecoveryShown,
  pendingDirectPaymentOrderId,
  rememberPendingDirectPayment,
} from "@/features/payments/directPayment.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { isNativeApp } from "@/shared/platform.ts";
import { Button } from "@/shared/ui";

// 포트원 공개 식별자(클라 노출 가능). 일반(즉시) 카드결제 결제창 재시도에 쓴다(체크아웃과 동일 규칙).
const PORTONE_STORE_ID = import.meta.env.VITE_PORTONE_STORE_ID as string | undefined;
const PORTONE_ONETIME_CHANNEL_KEY = (import.meta.env.VITE_PORTONE_CHANNEL_KEY_ONETIME ??
  import.meta.env.VITE_PORTONE_CHANNEL_KEY) as string | undefined;

// 주문 상세 화면 (상태/항목/취소).
export default function OrderDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const units = useUnits();
  const navState = location.state as {
    justCreated?: boolean;
    directPayError?: string | null;
  } | null;
  const justCreated = navState?.justCreated === true;
  // 일반(즉시) 카드결제 결제창 구매자 정보.
  const userId = useAuthStore((s) => s.session?.user.id ?? null);
  const displayName = useAuthStore((s) => s.session?.user.displayName ?? "");
  // 일반(즉시) 카드결제 진행/오류 상태(결제창 호출·서버 확인 중 표시).
  const [payBusy, setPayBusy] = useState(false);
  const directConfirmStarted = useRef(false);
  // 체크아웃에서 결제창을 못 열었으면 그 사유를 넘겨받아 초기 표시한다(상세에서 재시도 가능).
  const [payError, setPayError] = useState<string | null>(navState?.directPayError ?? null);

  const orderQuery = useQuery({
    queryKey: ["order", id],
    queryFn: () => getOrder(id),
    enabled: !!id,
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelOrder(id),
    onSuccess: (updated) => {
      qc.setQueryData(["order", id], updated);
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  // 일반 카드결제 복귀 또는 비정상 종료 후 재실행 복구 처리.
  //  - 모바일 결제창은 전체화면 리다이렉트로 떴다가 이 화면(redirectUrl)으로 돌아오며 쿼리에 결과를 붙인다.
  //    성공이면 paymentId/txId, 실패면 code/message. 서버가 포트원으로 최종 검증(confirm-direct)한다.
  useEffect(() => {
    if (!id) return;
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("paymentId");
    const txId = params.get("txId");
    const code = params.get("code");
    const pendingRecovery = pendingDirectPaymentOrderId() === id;
    if (pendingRecovery) markPendingDirectPaymentRecoveryShown(id);
    if (!paymentId && !txId && !code && !pendingRecovery) return;
    if (directConfirmStarted.current) return;
    directConfirmStarted.current = true;
    // 쿼리 정리(새로고침/뒤로가기 재처리 방지).
    window.history.replaceState({}, "", `/me/orders/${id}`);
    if (code) {
      clearPendingDirectPayment(id);
      setPayError(`결제가 완료되지 않았습니다. (${params.get("message") ?? code})`);
      return;
    }
    // 결제창 성공 추정 → 서버 확인.
    setPayBusy(true);
    confirmDirectPayment(id)
      .then((updated) => {
        clearPendingDirectPayment(id);
        qc.setQueryData(["order", id], updated);
        qc.invalidateQueries({ queryKey: ["orders"] });
      })
      .catch((err) =>
        setPayError(
          err instanceof ApiError
            ? err.message
            : "결제 확인에 실패했습니다. 잠시 후 다시 시도해 주세요.",
        ),
      )
      .finally(() => setPayBusy(false));
  }, [id, qc]);

  // 웹훅이 앱 복귀보다 먼저 결제완료로 동기화한 경우에도 남은 복구 표식을 정리한다.
  // 렌더 중 localStorage를 변경하지 않고 effect에서만 처리한다.
  useEffect(() => {
    if (orderQuery.data?.paymentStatus === "paid" && pendingDirectPaymentOrderId() === id) {
      clearPendingDirectPayment(id);
    }
  }, [id, orderQuery.data?.paymentStatus]);

  if (orderQuery.isLoading) {
    return (
      <main className="mx-auto flex max-w-screen-sm flex-col gap-4 px-4 py-8">
        <div className="h-40 animate-pulse rounded-lg bg-ink-100" />
      </main>
    );
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <main className="mx-auto flex max-w-screen-sm flex-col gap-4 px-4 py-8">
        <p className="text-base font-medium">주문을 찾을 수 없습니다.</p>
        <button
          type="button"
          onClick={() => navigate("/me/orders")}
          className="h-11 rounded-md border border-ink-300 text-sm font-medium hover:bg-ink-100"
        >
          주문 내역으로
        </button>
      </main>
    );
  }

  const order = orderQuery.data;

  const canCancel = order.status === ORDER_STATUS.RECEIVED;
  // 실중량 정산(PR-B): 무게가변 상품이 있고 아직 실중량 미확정이면 금액은 "예상"이다.
  //  - 일반(즉시) 카드결제는 주문 시 표시 금액으로 결제(금액 고정)되어 중량 정산이 없으므로 예상 안내를 하지 않는다.
  const isEstimate =
    order.paymentMethodType !== PAYMENT_METHOD_TYPE.INAPP_DIRECT &&
    order.items.some((it) => it.weightAdjustable) &&
    !order.weighedAt;
  // 주문 전체 총 중량(g). 실중량이 확정된 항목은 그 값을, 아니면 예상 중량(기본+옵션)을 더한다.
  const orderTotalWeightG = order.items.reduce(
    (sum, it) =>
      sum + (it.actualWeightG ?? lineExpectedGramsG(it.baseGramsSnapshot, it.quantity, it.options)),
    0,
  );
  // 일반(즉시) 카드결제인데 아직 결제 전(unpaid/failed)이면 손님이 결제를 마저 진행할 수 있게 한다.
  //  - 결제창을 닫았거나 실패해 미결제로 남은 주문의 재시도 경로다. 종결(취소/거절)된 주문은 제외.
  const needsDirectPayment =
    order.paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT &&
    (order.paymentStatus === "unpaid" || order.paymentStatus === "failed") &&
    order.status !== ORDER_STATUS.REJECTED &&
    order.status !== ORDER_STATUS.CANCELED;

  // 일반(즉시) 카드결제 결제창을 (다시) 띄운다. 성공 시 서버 확인까지 진행한다.
  async function payNow() {
    setPayError(null);
    if (
      !PORTONE_STORE_ID ||
      !PORTONE_ONETIME_CHANNEL_KEY ||
      PORTONE_STORE_ID.startsWith("REPLACE")
    ) {
      setPayError("결제 설정이 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.");
      return;
    }
    setPayBusy(true);
    rememberPendingDirectPayment(order.id);
    try {
      const res = await PortOne.requestPayment({
        storeId: PORTONE_STORE_ID,
        channelKey: PORTONE_ONETIME_CHANNEL_KEY,
        paymentId: stablePgPaymentId(order.id),
        orderName: `주문 ${order.orderNo}`,
        totalAmount: order.totalKrw,
        currency: "KRW",
        payMethod: "CARD",
        customer: userId
          ? { customerId: portoneCustomerId(userId), fullName: displayName.trim() || undefined }
          : undefined,
        redirectUrl: directPaymentRedirectUrl(order.id),
        // 네이티브 앱: 카드사 앱 인증 후 우리 앱으로 복귀할 커스텀 스킴(AndroidManifest=openchuksan).
        appScheme: isNativeApp ? NATIVE_PAYMENT_APP_SCHEME : undefined,
        noticeUrls: directPaymentNoticeUrls(),
      });
      // 리다이렉트 모드면 여기 도달 안 함. 팝업/iframe 결과 처리:
      if (!res) {
        // 사용자가 결제창을 닫음.
        setPayBusy(false);
        return;
      }
      if (res.code) {
        // PG/SDK 실패 사유를 그대로 노출(원인 파악 + 손님 안내).
        console.error("[payment] requestPayment 실패", res);
        setPayError(`결제가 완료되지 않았습니다: ${res.pgMessage ?? res.message ?? res.code}`);
        setPayBusy(false);
        return;
      }
      const updated = await confirmDirectPayment(order.id);
      clearPendingDirectPayment(order.id);
      qc.setQueryData(["order", id], updated);
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (err) {
      // 결제창 자체를 못 띄운 경우(설정/채널/네트워크 등)의 실제 메시지를 보존한다.
      console.error("[payment] requestPayment 예외", err);
      const detail = err instanceof Error ? err.message : String(err);
      setPayError(`결제를 진행하지 못했습니다: ${detail || "알 수 없는 오류"}`);
    } finally {
      setPayBusy(false);
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-28">
      {justCreated ? (
        <div className="rounded-lg border border-success-600/30 bg-success-50 p-4 text-sm text-success-700">
          <p className="text-base font-semibold">주문이 접수되었습니다.</p>
          <p className="mt-1 text-xs">
            매장에서 주문을 확인하면 알려드립니다. {customerPaymentNote(order)}
          </p>
        </div>
      ) : null}

      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs text-ink-600">주문번호</p>
          <p className="text-lg font-bold tabular-nums">{order.orderNo}</p>
        </div>
        <StatusBadge order={order} />
      </header>

      {/* 수령 정보 */}
      <section className="flex flex-col gap-1 rounded-lg border border-ink-200 p-4 text-sm">
        <Row label="주문 방식" value={ORDER_TYPE_LABEL_KO[order.type]} />
        <Row label="수령 시간" value={formatDesiredPickup(order.desiredDate, order.desiredTime)} />
        {order.delivery ? (
          <>
            <Row
              label="받는 분"
              value={`${order.delivery.recipientName} (${formatPhone(order.delivery.recipientPhone)})`}
            />
            <Row
              label="배송지"
              value={`${order.delivery.addressLine1}${order.delivery.addressLine2 ? ` ${order.delivery.addressLine2}` : ""}`}
            />
          </>
        ) : null}
        {order.customerMemo ? <Row label="요청사항" value={order.customerMemo} /> : null}
      </section>

      {/* 거절/취소 사유 */}
      {order.status === ORDER_STATUS.REJECTED && order.rejectReason ? (
        <section className="rounded-md border border-warning-600/30 bg-warning-50 p-3 text-sm text-warning-700">
          <p className="font-semibold">취소 안내</p>
          <p className="mt-1 text-xs">{order.rejectReason}</p>
        </section>
      ) : null}

      {/* 주문 상품 */}
      <section className="flex flex-col gap-2 rounded-lg border border-ink-200 p-4">
        <h2 className="text-sm font-semibold text-ink-700">주문 상품</h2>
        <ul className="flex flex-col gap-1.5">
          {order.items.map((it) => {
            // 예상 총중량(기본+추가옵션). 실중량이 확정되면 그 값을 대신 보여준다.
            const expectedG = lineExpectedGramsG(it.baseGramsSnapshot, it.quantity, it.options);
            return (
              <li key={it.id} className="flex flex-col gap-0.5 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate text-ink-800">
                    {it.name}{" "}
                    <span className="text-xs text-ink-600">
                      ({formatCartLineLabel(units, it.unit, it.quantity)})
                    </span>
                    {it.actualWeightG ? (
                      <span className="ml-1 text-xs text-brand-700">실 {it.actualWeightG}g</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 tabular-nums text-ink-800">
                    {it.lineTotalKrw.toLocaleString()}원
                  </span>
                </div>
                {/* 추가 옵션(금액은 위 줄 합계에 포함). */}
                {it.options.map((o, oi) => (
                  <div key={oi} className="pl-3 text-xs text-ink-600">
                    + {o.label} x{o.quantity}
                    {o.addGrams > 0 ? ` (${formatWeightG(o.addGrams * o.quantity)})` : ""}
                  </div>
                ))}
                {!it.actualWeightG && expectedG > 0 && it.options.length > 0 ? (
                  <div className="pl-3 text-xs text-ink-500">총 {formatWeightG(expectedG)}</div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <div className="mt-2 border-t border-ink-200 pt-2 text-sm">
          {orderTotalWeightG > 0 ? (
            <Row label="총 중량" value={formatWeightG(orderTotalWeightG)} muted />
          ) : null}
          <Row label="상품 합계" value={`${order.subtotalKrw.toLocaleString()}원`} muted />
          {order.discountKrw > 0 ? (
            <Row label="쿠폰 할인" value={`-${order.discountKrw.toLocaleString()}원`} muted />
          ) : null}
          {order.complimentDiscountKrw > 0 ? (
            <Row
              label="칭찬 감사 할인"
              value={`-${order.complimentDiscountKrw.toLocaleString()}원`}
              muted
            />
          ) : null}
          {order.pointsUsedKrw > 0 ? (
            <Row label="적립금 사용" value={`-${order.pointsUsedKrw.toLocaleString()}원`} muted />
          ) : null}
          {order.type === "delivery" ? (
            <Row
              label="배달비"
              value={
                order.deliveryFeeKrw === 0 ? "무료" : `${order.deliveryFeeKrw.toLocaleString()}원`
              }
              muted
            />
          ) : null}
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-base font-semibold">
              {order.paymentStatus === "paid"
                ? "결제 금액"
                : order.paymentStatus === "refunded"
                  ? "환불 금액"
                  : isEstimate
                    ? "예상 결제 금액"
                    : "결제 예정 금액"}
            </span>
            <span className="text-2xl font-bold tabular-nums">
              {order.totalKrw.toLocaleString()}원
            </span>
          </div>
        </div>
        {isEstimate ? (
          <p className="text-xs text-ink-600">
            정육 등 무게로 파는 상품은 실제 손질 중량에 따라 최종 금액이 조금 달라질 수 있어요.
            준비완료 안내 때 최종 금액을 알려드립니다.
          </p>
        ) : null}
        <p className="text-xs text-ink-600">{customerPaymentNote(order)}</p>
      </section>

      {/* 일반(즉시) 카드결제 미완료: 결제를 마저 진행하도록 안내한다(결제 전 주문은 매장이 준비할 수 없음). */}
      {needsDirectPayment ? (
        <section className="flex flex-col gap-2 rounded-lg border border-brand-300 bg-brand-50 p-4">
          <p className="text-sm font-semibold text-brand-800">결제를 완료해 주세요</p>
          <p className="text-xs text-brand-700">
            카드 결제가 아직 완료되지 않았습니다. 아래 버튼으로 결제를 마치면 매장에서 주문을
            준비합니다.
          </p>
          <Button type="button" onClick={payNow} loading={payBusy} disabled={payBusy}>
            {payBusy ? "결제 진행 중..." : `${order.totalKrw.toLocaleString()}원 카드 결제하기`}
          </Button>
        </section>
      ) : null}

      {payError ? (
        <p className="rounded-md border border-danger-500/30 bg-danger-50 p-3 text-sm text-danger-700">
          {payError}
        </p>
      ) : payBusy && !needsDirectPayment ? (
        <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-sm text-ink-600">
          결제를 확인하고 있습니다...
        </p>
      ) : null}

      {cancelMut.isError ? (
        <p className="rounded-md border border-warning-600/30 bg-warning-50 p-3 text-sm text-warning-700">
          {cancelMut.error instanceof ApiError
            ? cancelMut.error.message
            : "주문을 취소하지 못했습니다."}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => navigate("/me/orders")}
          className="h-11 flex-1 rounded-md border border-ink-300 text-sm font-medium hover:bg-ink-100"
        >
          주문 내역
        </button>
        {canCancel ? (
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: "주문 취소",
                description: "주문을 취소할까요?",
                confirmLabel: "주문 취소",
                cancelLabel: "닫기",
              });
              if (ok) {
                cancelMut.mutate();
              }
            }}
            disabled={cancelMut.isPending}
            className="h-11 flex-1 rounded-md border border-ink-400 text-sm font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
          >
            주문 취소
          </button>
        ) : null}
      </div>
    </main>
  );
}

// 상태 배지. 손님 라벨맵 사용 (거절/취소 모두 "취소").
function StatusBadge({ order }: { order: OrderDto }) {
  const label = ORDER_STATUS_LABEL_KO[order.status];
  const isCanceled =
    order.status === ORDER_STATUS.REJECTED || order.status === ORDER_STATUS.CANCELED;
  const isDone = order.status === ORDER_STATUS.COMPLETED;
  const className = isCanceled
    ? "bg-ink-200 text-ink-600"
    : isDone
      ? "bg-success-50 text-success-700"
      : "bg-brand-600 text-white";
  return (
    <span className={`rounded-full px-3 py-1 text-sm font-semibold ${className}`}>{label}</span>
  );
}

// 상세 행.
function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className={`shrink-0 ${muted ? "text-ink-600" : "text-ink-600"}`}>{label}</span>
      <span className={`text-right ${muted ? "tabular-nums text-ink-700" : "text-ink-900"}`}>
        {value}
      </span>
    </div>
  );
}

// 손님용 결제 안내 문구(결제수단/상태). 부정/판단적 표현 회피([[feedback-customer-copy-tone]]):
//  실패/처리중 같은 내부 상태는 손님에게 그대로 노출하지 않고 중립적 안내로 바꾼다.
function customerPaymentNote(order: OrderDto): string {
  const m = order.paymentMethodType;
  const s = order.paymentStatus;
  if (s === "paid") {
    return m === "inapp" ? "등록하신 카드로 결제되었습니다." : "결제가 완료되었습니다.";
  }
  if (s === "refunded") {
    return "결제 금액이 환불되었습니다.";
  }
  if (s === "refund_failed") {
    return "환불을 처리하고 있습니다.";
  }
  // unpaid/charging/failed = 아직 결제 전. 결제수단별 예정 안내(중립적).
  if (m === "inapp_direct") {
    return "카드 결제를 완료하면 매장에서 주문을 준비합니다.";
  }
  if (m === "inapp") {
    return "준비 완료 시 등록하신 카드로 결제됩니다.";
  }
  if (m === "sms_request") {
    return "준비 완료 시 문자(SMS)로 결제 안내를 보내드립니다.";
  }
  return "수령 시 현장에서 결제됩니다.";
}

// 휴대폰 표시 포맷.
function formatPhone(p: string): string {
  if (p.length === 11) return `${p.slice(0, 3)}-${p.slice(3, 7)}-${p.slice(7)}`;
  if (p.length === 10) return `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`;
  return p;
}
